import { useState } from "react";
import { Plus } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import useAdminLoad from "@/hooks/useAdminLoad";
import { Empty, LoadState, Panel, Pill } from "@/components/admin/AdminUI";
import CreateWaveDialog from "@/components/admin/launch/CreateWaveDialog";
import GenderSplit from "@/components/admin/launch/GenderSplit";
import WaveDetail from "@/components/admin/launch/WaveDetail";
import { formatDate } from "@/lib/format";

export const WAVE_TONE = { draft: "neutral", sent: "brand", opened: "success" };

function WaveRow({ w, onOpen }) {
  return (
    <li>
      <button onClick={() => onOpen(w.id)} className="grid w-full gap-4 px-5 py-5 text-left transition-colors hover:bg-[var(--cw-surface)] md:grid-cols-[1.3fr_1fr_1.6fr]" data-testid={`wave-row-${w.id}`}>
        <div className="min-w-0">
          <div className="flex items-center gap-2"><p className="font-medium">{w.name}</p><Pill tone={WAVE_TONE[w.status]} className="capitalize" data-testid={`wave-status-${w.id}`}>{w.status}</Pill>{!w.rule_ok && <Pill tone="warning">Over gender share</Pill>}</div>
          <p className="cw-muted mt-1 truncate text-xs">{w.zips.join(", ")} · max {w.max_gender_pct}% · created {formatDate(w.created_at, false)}</p>
        </div>
        <div className="text-sm tabular-nums">
          <p><span className="font-semibold">{w.size}</span><span className="cw-muted"> / {w.target_size} picked</span></p>
          <p className="cw-soft" data-testid={`wave-counts-${w.id}`}>{w.invited} invited · {w.signed_up} signed up</p>
        </div>
        <GenderSplit items={w.gender_split} max={w.max_gender_pct} testId={`wave-split-${w.id}`} />
      </button>
    </li>
  );
}

export default function WavesTab() {
  const { data, error, loading, reload } = useAdminLoad("/admin/waves");
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  if (openId) return <WaveDetail id={openId} onBack={() => { setOpenId(null); reload(); }} />;
  return (
    <div className="space-y-4" data-testid="launch-waves-tab">
      <div className="flex items-center justify-between gap-3">
        <p className="cw-soft text-sm">Invite people in batches. Each person gets a single-use code by email; test addresses are only logged.</p>
        <Button onClick={() => setCreating(true)} data-testid="wave-create-open-button"><Plus />New wave</Button>
      </div>
      <LoadState loading={loading} error={error} onRetry={reload}>
        <Panel className="overflow-hidden">
          {data?.items?.length ? <ul className="divide-y divide-border" data-testid="waves-list">{data.items.map((w) => <WaveRow key={w.id} w={w} onOpen={setOpenId} />)}</ul>
            : <Empty title="No waves yet" text="Create one to pick people from the waitlist." testId="waves-empty" />}
        </Panel>
      </LoadState>
      <CreateWaveDialog open={creating} onOpenChange={setCreating} onCreated={(w) => setOpenId(w.id)} />
    </div>
  );
}
